import { QueryKey } from "react-query";
import { logReactQueryError } from "./errorLogger";

interface RefetchEvent {
  timestamp: number;
  queryKey: QueryKey;
}

/**
 * Monitors query refetch frequency to detect infinite refetch loops
 */
class RefetchMonitor {
  private refetchHistory: Map<string, RefetchEvent[]> = new Map();
  private readonly maxRefetches: number;
  private readonly timeWindow: number;

  constructor(maxRefetches = 5, timeWindowMs = 10000) {
    this.maxRefetches = maxRefetches;
    this.timeWindow = timeWindowMs;
  }

  /**
   * Records a refetch event for a query
   * Returns true if this query is refetching excessively (infinite loop detected)
   */
  recordRefetch(queryKey: QueryKey): boolean {
    const keyString = JSON.stringify(queryKey);
    const now = Date.now();

    // Get or create history for this query
    if (!this.refetchHistory.has(keyString)) {
      this.refetchHistory.set(keyString, []);
    }

    const history = this.refetchHistory.get(keyString);

    // Remove events older than the time window
    const recentEvents = history.filter(
      (event) => now - event.timestamp < this.timeWindow
    );

    // Add current event
    recentEvents.push({ timestamp: now, queryKey });

    // Update history
    this.refetchHistory.set(keyString, recentEvents);

    // Check if we've exceeded the threshold
    if (recentEvents.length > this.maxRefetches) {
      const errorContext = {
        queryKey,
        refetchCount: recentEvents.length,
        timeWindow: `${this.timeWindow}ms`,
        threshold: this.maxRefetches,
        timestamps: recentEvents.map((e) =>
          new Date(e.timestamp).toISOString()
        ),
      };

      // Log to console (development)
      console.error("[🚨][Infinite Refetch Detected]", errorContext);

      // Log to Rollbar (production)
      logReactQueryError(
        new Error(
          `Infinite refetch detected: ${JSON.stringify(queryKey)} refetched ${recentEvents.length} times in ${this.timeWindow}ms`
        ),
        {
          type: "query",
          key: queryKey,
        }
      );

      return true; // Infinite loop detected
    }

    return false;
  }

  /**
   * Reset tracking for a specific query
   */
  reset(queryKey: QueryKey): void {
    const keyString = JSON.stringify(queryKey);
    this.refetchHistory.delete(keyString);
  }

  /**
   * Clear all tracking data
   */
  clearAll(): void {
    this.refetchHistory.clear();
  }

  /**
   * Get current refetch count for a query within the time window
   */
  getRefetchCount(queryKey: QueryKey): number {
    const keyString = JSON.stringify(queryKey);
    const history = this.refetchHistory.get(keyString);

    if (!history) {
      return 0;
    }

    const now = Date.now();
    return history.filter((event) => now - event.timestamp < this.timeWindow)
      .length;
  }
}

// Singleton instance
export const refetchMonitor = new RefetchMonitor(5, 10000); // 5 refetches in 10 seconds

